import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { Flame, Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function VerifyEmail() {
  const { checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [status, setStatus] = useState(token ? "checking" : "no-token"); // checking | ok | error
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        // Refetch /auth/me so AuthContext's cached user (and the Dashboard banner reading it) picks up
        // email_verified: true right away, instead of only after the next hard reload.
        await checkAuth();
        setStatus("ok");
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
        setStatus("error");
      }
    })();
  }, [token, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="verify-email-logo">
          <span className="w-9 h-9 rounded-md bg-primary flex items-center justify-center"><Flame className="w-5 h-5 text-white" /></span>
          <span className="font-display text-xl font-bold">DealWisp</span>
        </Link>
        <div className="bg-card border border-white/10 rounded-2xl p-8 text-center">
          {status === "no-token" && (
            <p className="text-sm text-primary" data-testid="verify-email-no-token">
              This link is missing its token. Request a new one from your dashboard.
            </p>
          )}
          {status === "checking" && <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" data-testid="verify-email-checking" />}
          {status === "ok" && (
            <>
              <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
              <h1 className="font-display text-xl font-bold tracking-tight">Email verified</h1>
              <p className="text-sm text-muted-foreground mt-2" data-testid="verify-email-ok">You're all set.</p>
              <Link to="/dashboard"><Button className="mt-5 rounded-full bg-primary hover:bg-secondary" data-testid="verify-email-to-dashboard">Go to dashboard</Button></Link>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle className="w-10 h-10 text-primary mx-auto mb-3" />
              <h1 className="font-display text-xl font-bold tracking-tight">Couldn't verify</h1>
              <p className="text-sm text-muted-foreground mt-2" data-testid="verify-email-error">{error}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
